import type { EventType } from '@cocoonspace/sdk-js/v2/types/eventType'
import { createContext, useContext, useMemo, useState } from 'react'
import { useGetEventType } from '@cocoonspace/sdk-js/v2/domains/schedulers/schedulers'

import { LoadingCard } from './loading-card'
import { NotFound } from './not-found'

type AnswerCardStep = 'dateTime' | 'validation'

interface AnswerCardContextType {
	step: AnswerCardStep
	setStep: (step: AnswerCardStep) => void
	eventType: EventType
	currentMonth: Date
	setCurrentMonth: (date: Date) => void
}

const AnswerCardContext = createContext<AnswerCardContextType | undefined>(
	undefined,
)

export const AnswerCardProvider = ({
	children,
	idOrSlug,
}: {
	children: React.ReactNode
	idOrSlug: string
}) => {
	const [step, setStep] = useState<AnswerCardStep>('dateTime')
	const [currentMonth, setCurrentMonth] = useState(() => new Date())
	const eventTypeQuery = useGetEventType(idOrSlug)

	const value = useMemo(
		() => ({
			step,
			setStep,
			eventType: eventTypeQuery.data,
			currentMonth,
			setCurrentMonth,
		}),
		[step, eventTypeQuery.data, currentMonth],
	)

	if (eventTypeQuery.isPending) return <LoadingCard />
	if (eventTypeQuery.isError) return <NotFound />

	return (
		<AnswerCardContext.Provider value={value as AnswerCardContextType}>
			{children}
		</AnswerCardContext.Provider>
	)
}

export const useAnswerCard = () => {
	const context = useContext(AnswerCardContext)

	if (!context) {
		throw new Error('useAnswerCard must be used within an AnswerCardProvider')
	}

	return context
}
